import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseWooCommerceV2ApiService } from './base-woo-commerce-v2-api.service';
import { WooCommerceShippingParameters } from '../../../interfaces/woo-commerce/shipping/shipping.interface';
import {
  WooCommerceShipping,
  WooCommerceShippingMethod
} from '../../../interfaces/woo-commerce/api/shipping/shipping-api.interface';
import { WooCommerceShippingAddress } from '../../../interfaces/woo-commerce/api/woo-commerce-api.interface';
import { catchError, map, Observable, of } from 'rxjs';
import { mapHappyPickupLocationsToWooShippingAddresses } from '../../happy/happy-api-mappers';
import {
  HappyDeliveryCheckRequest,
  HappyDeliveryCheckResponse,
  HappyDeliveryMethod,
  HappyApiResponse
} from '../../../interfaces/happy-api/happy-api.interface';
import {
  HappyStoreConfig,
  HappyStoreSelectionService
} from '../../happy/happy-store-selection.service';

@Injectable({ providedIn: 'root' })
export class WooCommerceShippingApiService extends BaseWooCommerceV2ApiService {
  constructor(
    httpClient: HttpClient,
    private happyStoreSelectionService: HappyStoreSelectionService
  ) {
    super(httpClient);
  }

  deliveryCheck(
    body: HappyDeliveryCheckRequest
  ): Observable<HappyDeliveryCheckResponse> {
    return this.post<HappyApiResponse<HappyDeliveryCheckResponse>>(
      '/delivery-check',
      body
    ).pipe(map((response) => response.data));
  }

  /**
   * Map delivery-check zones/methods into the WooCommerceShippingMethod shape
   * so existing UI code (ShippingOption mapping, card selectors) keeps working.
   */
  static mapDeliveryCheckToShippingMethods(
    response: HappyDeliveryCheckResponse
  ): WooCommerceShippingMethod[] {
    if (!response.is_deliverable) {
      return [];
    }
    return response.zones.flatMap((zone) =>
      zone.methods.map(
        (method: HappyDeliveryMethod): WooCommerceShippingMethod => {
          const taxStatus = method.settings?.taxable ? 'taxable' : 'none';
          const cost = String(method.fee_amount ?? 0);
          return {
            plugin_id: '',
            id: String(method.method_id),
            errors: [],
            settings: method.settings || {},
            supports: [],
            method_title: method.method_name,
            method_description: '',
            enabled: 'yes',
            title: method.method_name,
            rates: [],
            tax_status: taxStatus,
            fee: null,
            minimum_fee: null,
            instance_id: method.method_id,
            instance_settings: {
              title: method.method_name,
              tax_status: taxStatus,
              cost
            },
            availability: null,
            countries: [],
            method_order: 0,
            has_settings: false,
            cost
          };
        }
      )
    );
  }

  getShippingMethods(
    parameters: WooCommerceShippingParameters
  ): Observable<WooCommerceShipping> {
    return this.get('/cart/shipping-options').pipe(
      map((response: any) => {
        const data = response?.data || response || {};
        const deliveryMethods = Array.isArray(data?.delivery_methods)
          ? data.delivery_methods
          : Array.isArray(data) ? data : [];

        return {
          user_id: 0,
          package: {
            destination: {
              state: parameters?.state || '',
              postcode: parameters?.postcode || '',
              country: parameters?.country || ''
            }
          },
          zone_id: 0,
          zone_name: '',
          zone_locations: [],
          zone_formatted_location: '',
          shipping_methods: deliveryMethods.map((option: any) => ({
            plugin_id: '',
            id: String(option?.id || option?.zone_shipping_method_id || ''),
            errors: [],
            settings: {},
            supports: [],
            method_title: option?.name || option?.method_title || '',
            method_description: '',
            enabled: 'yes',
            title: option?.name || option?.method_title || '',
            rates: [],
            tax_status: 'taxable',
            fee: null,
            minimum_fee: null,
            instance_id: Number(
              option?.zone_shipping_method_id || option?.id || 0
            ),
            instance_settings: {
              title: option?.name || option?.method_title || '',
              tax_status: 'taxable',
              cost: String(option?.fee_amount || option?.price || '0')
            },
            availability: null,
            countries: [],
            method_order: 0,
            has_settings: false,
            cost: String(option?.fee_amount || option?.price || '0')
          }))
        } as WooCommerceShipping;
      })
    );
  }

  /**
   * Returns pickup locations for the **currently selected** store only.
   *
   * Previously we aggregated pickup locations across every store configured in
   * `environment.happyApi.stores`, which let the user switch stores by picking
   * another location's pickup point. Per product direction, the storefront
   * should now show only the selected store's pickup locations — users switch
   * stores via the store-picker dialog (or a long-tail URL), not via the
   * pickup list.
   */
  getPickupLocations(): Observable<WooCommerceShippingAddress[]> {
    const selectedStore = this.happyStoreSelectionService.getSelected();
    if (!selectedStore) {
      return this.getPickupLocationsForCurrentStore();
    }

    const selectedIndex = this.happyStoreSelectionService.getSelectedIndex();
    return this.getPickupLocationsForStore(selectedStore, selectedIndex).pipe(
      catchError(() => of([] as WooCommerceShippingAddress[]))
    );
  }

  private getPickupLocationsForCurrentStore(): Observable<WooCommerceShippingAddress[]> {
    return this.get('/cart/shipping-options').pipe(
      map((response: any) => {
        const data = response?.data || response || {};
        const rawLocations = Array.isArray(data?.pickup_locations)
          ? data.pickup_locations
          : [];
        return mapHappyPickupLocationsToWooShippingAddresses(rawLocations);
      })
    );
  }

  private getPickupLocationsForStore(
    store: HappyStoreConfig,
    index: number
  ): Observable<WooCommerceShippingAddress[]> {
    const tenant = store.tenant || '';
    const storeApiKey = store.storeApiKey || '';

    return this.get('/cart/shipping-options', {
      params: {
        tenant,
        store_api_key: storeApiKey
      }
    }).pipe(
      map((response: any) => {
        const data = response?.data || response || {};
        const rawLocations = Array.isArray(data?.pickup_locations)
          ? data.pickup_locations
          : [];

        return mapHappyPickupLocationsToWooShippingAddresses(rawLocations, {
          index,
          domain: store.domain,
          name: store.name,
          tenant,
          storeApiKey
        });
      })
    );
  }
}
